import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface UseResizableComposerOptions {
  open: boolean;
  containerRef: RefObject<HTMLElement | null>;
  defaultHeight?: number;
  minHeight?: number;
  reservedAbove?: number;
  storageKey?: string;
}

export function useResizableComposer({
  open,
  containerRef,
  defaultHeight = 340,
  minHeight = 220,
  reservedAbove = 180,
  storageKey,
}: UseResizableComposerOptions) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<{ y: number; height: number } | null>(null);

  const [height, setHeight] = useState(() => {
    if (!storageKey || typeof window === "undefined") return defaultHeight;
    const stored = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 ? stored : defaultHeight;
  });
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(open);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    setMounted(true);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  const maxHeight = useCallback(() => {
    const available = containerRef.current?.clientHeight ?? window.innerHeight;
    return Math.max(minHeight, available - reservedAbove);
  }, [containerRef, minHeight, reservedAbove]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragOrigin.current = { y: event.clientY, height: panelRef.current?.offsetHeight ?? height };
      setDragging(true);
    },
    [height],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = dragOrigin.current;
      if (!origin || !panelRef.current) return;
      const next = clamp(origin.height + (origin.y - event.clientY), minHeight, maxHeight());
      panelRef.current.style.height = `${next}px`;
    },
    [maxHeight, minHeight],
  );

  const endDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragOrigin.current) return;
      event.currentTarget.releasePointerCapture(event.pointerId);
      dragOrigin.current = null;
      const settled = panelRef.current?.offsetHeight ?? height;
      setHeight(settled);
      setDragging(false);
      if (storageKey) window.localStorage.setItem(storageKey, String(settled));
    },
    [height, storageKey],
  );

  useEffect(() => {
    if (!entered) return;
    const onResize = () => setHeight((current) => clamp(current, minHeight, maxHeight()));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [entered, maxHeight, minHeight]);

  return {
    panelRef,
    mounted,
    entered,
    dragging,
    height,
    panelProps: {
      ref: panelRef,
      style: { height: entered ? height : 0 },
      onTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== "height") return;
        if (!entered) setMounted(false);
      },
    },
    handleProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag },
  };
}
